import { useEffect } from 'react';

import { PaginatedTable } from '@/components/PaginatedTable';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useRecentResourceChanges } from '@/hooks/discovery/recentResourceChanges';
import { useLatestChangesColumns } from '../hooks/useLatestChangesColumns';

const PAGE_SIZE = 5;

type LatestChangesProps = {
  /** Used for stubbing `new Date()` in testing */
  now?: Date;
  setLastRunAt: (lastRunAt: string | null) => void;
  setLastChangedAt: (lastChangedAt: string | null) => void;
};

export const LatestChanges = ({
  setLastRunAt,
  setLastChangedAt,
  ...props
}: LatestChangesProps) => {
  const { environmentId } = useDbtCloudProject();

  const recentResourceChanges = useRecentResourceChanges({
    pageSize: PAGE_SIZE,
    environmentId,
  });

  const disableGroupsColumn =
    !!recentResourceChanges.data &&
    !recentResourceChanges.data.some(
      (model) => model.resource && 'group' in model.resource && model.resource.group,
    );
  const columns = useLatestChangesColumns({ ...props, disableGroupsColumn });
  const changedAt = recentResourceChanges.data?.at(0)?.mostRecentChangedAt || null;
  const runAt = recentResourceChanges.lastUpdatedAt || null;
  const isLoading = recentResourceChanges.fetching;
  // TODO: fix flicker when switching from issues to changes table
  useEffect(() => {
    if (!isLoading) {
      setLastChangedAt(changedAt);
      setLastRunAt(runAt);
    }
  }, [changedAt, runAt, isLoading, setLastChangedAt, setLastRunAt]);

  return (
    <div>
      <PaginatedTable
        data={recentResourceChanges.data ?? []}
        isLoading={recentResourceChanges.fetching}
        maxResultCount={undefined}
        pageSize={PAGE_SIZE}
        isSortable={false}
        pagination={{
          hasMorePages: recentResourceChanges.pageInfo?.hasNextPage,
          isPageLoading: recentResourceChanges.fetching,
          onLoadMore: recentResourceChanges.fetchMore,
        }}
        columns={columns}
      />
    </div>
  );
};
